/**
 * AdminView — user account management console (admin-only).
 *
 * The client/billing backend will live here once a payment provider is
 * integrated; for now it manages accounts: role, disabled state, delete.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

import { BACKEND_URL, logDev } from "../lib/beaconUtils";

const ADMIN_API = `${BACKEND_URL}/api/admin`;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function AdminView({ authUser }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [waitlist, setWaitlist] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, u, w] = await Promise.all([
        axios.get(`${ADMIN_API}/stats`, { withCredentials: true }),
        axios.get(`${ADMIN_API}/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/beacon/waitlist`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setUsers(u.data.users);
      setWaitlist(w.data.entries);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      logDev("Admin load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchUser = async (userId, body) => {
    setBusyId(userId);
    try {
      await axios.patch(`${ADMIN_API}/users/${userId}`, body, {
        withCredentials: true,
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (userId) => {
    setBusyId(userId);
    try {
      await axios.delete(`${ADMIN_API}/users/${userId}`, {
        withCredentials: true,
      });
      setConfirmDeleteId(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusyId(null);
    }
  };

  if (error) {
    return (
      <div className="beacon-alert" data-testid="admin-error">
        Admin console unavailable: {error}
      </div>
    );
  }

  return (
    <div className="beacon-admin" data-testid="admin-view">
      <div className="beacon-dashboard-head">
        <div>
          <div className="beacon-tiny-label">ADMIN CONSOLE</div>
          <h2 className="beacon-dashboard-title">User accounts</h2>
        </div>
      </div>

      {stats && (
        <div className="beacon-dashboard-stats" data-testid="admin-stats">
          <div className="beacon-dashboard-stat">
            <span className="beacon-dashboard-stat-num" data-testid="admin-stat-users">
              {stats.users_total}
            </span>
            <span className="beacon-dashboard-stat-label">Accounts</span>
          </div>
          <div className="beacon-dashboard-stat">
            <span className="beacon-dashboard-stat-num" data-testid="admin-stat-admins">
              {stats.admins}
            </span>
            <span className="beacon-dashboard-stat-label">Admins</span>
          </div>
          <div className="beacon-dashboard-stat">
            <span className="beacon-dashboard-stat-num" data-testid="admin-stat-sessions">
              {stats.active_sessions}
            </span>
            <span className="beacon-dashboard-stat-label">Active sessions</span>
          </div>
          <div className="beacon-dashboard-stat">
            <span className="beacon-dashboard-stat-num" data-testid="admin-stat-packs">
              {stats.packs_live}
            </span>
            <span className="beacon-dashboard-stat-label">Live packs</span>
          </div>
        </div>
      )}

      <div className="beacon-admin-table-wrap" data-testid="admin-user-table">
        {loading ? (
          <div className="beacon-dashboard-loading">
            <span className="beacon-spinner beacon-spinner-dark" />
          </div>
        ) : (
          <table className="beacon-admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Tier</th>
                <th>Access</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Last login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.user_id === authUser?.user_id;
                const locked = u.is_primary_admin || isSelf;
                const busy = busyId === u.user_id;
                return (
                  <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}
                    className={u.disabled ? "is-disabled" : ""}>
                    <td>
                      <div className="beacon-admin-user-name">
                        {u.name}
                        {u.is_primary_admin && (
                          <span className="beacon-admin-primary-chip"
                            data-testid={`primary-chip-${u.user_id}`}>
                            primary admin
                          </span>
                        )}
                        {isSelf && !u.is_primary_admin && (
                          <span className="beacon-admin-you-chip">you</span>
                        )}
                      </div>
                      <div className="beacon-admin-user-email">{u.email}</div>
                    </td>
                    <td>
                      <span className={`beacon-admin-role is-${u.role}`}>
                        {u.role}
                      </span>
                    </td>
                    <td>
                      <button type="button"
                        className={`beacon-tier-chip beacon-tier-toggle is-${u.plan_tier}`}
                        data-testid={`admin-toggle-tier-${u.user_id}`}
                        disabled={busy || u.is_primary_admin}
                        title={u.is_primary_admin
                          ? "The primary admin is always Pro"
                          : "Toggle Basic / Pro plan tier"}
                        onClick={() => patchUser(u.user_id, {
                          plan_tier: u.plan_tier === "pro" ? "basic" : "pro",
                        })}>
                        {u.plan_tier}
                      </button>
                    </td>
                    <td>
                      <button type="button"
                        className={`beacon-admin-status beacon-activation-toggle ${u.activated ? "is-on" : "is-pending"}`}
                        data-testid={`admin-toggle-activated-${u.user_id}`}
                        disabled={busy || (locked && u.activated)}
                        title={locked && u.activated
                          ? "Protected account"
                          : u.activated ? "Revoke access" : "Activate this account"}
                        onClick={() => patchUser(u.user_id, {
                          activated: !u.activated,
                        })}>
                        {u.activated ? "active" : "activate →"}
                      </button>
                    </td>
                    <td>
                      <span className={`beacon-admin-status ${u.disabled ? "is-off" : "is-on"}`}>
                        {u.disabled ? "disabled" : "active"}
                      </span>
                    </td>
                    <td>{fmtDate(u.created_at)}</td>
                    <td>{fmtDate(u.last_login_at)}</td>
                    <td className="beacon-admin-actions">
                      <button type="button" className="beacon-secondary-btn beacon-admin-btn"
                        data-testid={`admin-toggle-role-${u.user_id}`}
                        disabled={busy || (locked && u.role === "admin")}
                        title={locked && u.role === "admin"
                          ? "Protected account"
                          : undefined}
                        onClick={() => patchUser(u.user_id, {
                          role: u.role === "admin" ? "user" : "admin",
                        })}>
                        {u.role === "admin" ? "Demote" : "Make admin"}
                      </button>
                      <button type="button" className="beacon-secondary-btn beacon-admin-btn"
                        data-testid={`admin-toggle-disabled-${u.user_id}`}
                        disabled={busy || (locked && !u.disabled)}
                        title={locked && !u.disabled ? "Protected account" : undefined}
                        onClick={() => patchUser(u.user_id, { disabled: !u.disabled })}>
                        {u.disabled ? "Enable" : "Disable"}
                      </button>
                      {confirmDeleteId === u.user_id ? (
                        <>
                          <button type="button"
                            className="beacon-secondary-btn beacon-admin-btn beacon-admin-btn-danger"
                            data-testid={`admin-confirm-delete-${u.user_id}`}
                            disabled={busy}
                            onClick={() => deleteUser(u.user_id)}>
                            Confirm delete
                          </button>
                          <button type="button" className="beacon-secondary-btn beacon-admin-btn"
                            data-testid={`admin-cancel-delete-${u.user_id}`}
                            onClick={() => setConfirmDeleteId(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button type="button"
                          className="beacon-secondary-btn beacon-admin-btn beacon-admin-btn-danger"
                          data-testid={`admin-delete-${u.user_id}`}
                          disabled={busy || locked}
                          title={locked ? "Protected account" : undefined}
                          onClick={() => setConfirmDeleteId(u.user_id)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <section className="beacon-dashboard-card beacon-admin-waitlist"
        data-testid="admin-waitlist">
        <div className="beacon-tiny-label">
          PRICING WAITLIST · {waitlist.length} REGISTRATION{waitlist.length === 1 ? "" : "S"}
        </div>
        {waitlist.length === 0 ? (
          <p className="beacon-dashboard-empty">
            No registrations yet — interest captured on the /pricing page
            lands here.
          </p>
        ) : (
          <ul className="beacon-admin-waitlist-list">
            {waitlist.map((w) => (
              <li key={w.email} data-testid={`waitlist-entry-${w.email}`}>
                <span className="beacon-admin-waitlist-email">{w.email}</span>
                {w.name && <span className="beacon-admin-waitlist-name">{w.name}</span>}
                {w.tier && (
                  <span className={`beacon-tier-chip is-${w.tier}`}>{w.tier}</span>
                )}
                <span className="beacon-admin-waitlist-date">{fmtDate(w.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="beacon-dashboard-card beacon-admin-billing"
        data-testid="admin-billing-placeholder">
        <div className="beacon-tiny-label">BILLING &amp; SUBSCRIPTIONS</div>
        <p className="beacon-dashboard-empty">
          Client billing will run from here once a payment provider is
          connected — plans, invoices and subscription status per account.
        </p>
      </section>
    </div>
  );
}
